/**
 * API Client - Base fetch wrapper with authentication
 * Uses Auth0 tokens for authentication
 */

const API_URL = import.meta.env.VITE_API_URL;

// Token getter function - will be set by the AuthContext
let tokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the token getter function (called by AuthContext on mount)
 */
export function setTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** User-friendly message suitable for display in UI banners */
  get userMessage(): string {
    if (this.status === 401) {
      return 'Your session has expired. Please refresh the page or open directory.aiu3.net in a new tab to reauthenticate.';
    }
    return this.message;
  }
}

/** Extract a user-friendly error message from any caught error */
export function getUserErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.userMessage;
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Base fetch wrapper with authentication and error handling
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_URL}${endpoint}`;

  // Get token dynamically from Auth0
  const token = tokenGetter ? await tokenGetter() : null;

  // Build headers with auth and optional impersonation
  const impersonationUserId = localStorage.getItem('impersonation_user_id');
  // Let the browser set the multipart boundary for file uploads
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(impersonationUserId ? { 'X-Impersonate-User-Id': impersonationUserId } : {}),
    ...options.headers,
  };

  if (import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true') {
    console.log(`[API] ${options.method || 'GET'} ${endpoint}`);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle non-2xx responses
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData;

      try {
        errorData = await response.json();
        // Handle validation errors (detail is an array of error objects)
        if (Array.isArray(errorData.detail)) {
          // Extract readable messages from validation errors
          const messages = errorData.detail.map((err: any) => {
            const field = err.loc?.slice(1).join('.') || 'field';
            return `${field}: ${err.msg}`;
          });
          errorMessage = messages.join('; ');
        } else {
          errorMessage = errorData.detail || errorData.message || errorMessage;
        }
      } catch {
        // Response wasn't JSON, use status text
      }

      throw new ApiError(errorMessage, response.status, errorData);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    // Parse JSON response
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    console.error('[API] Request failed:', error);
    throw new ApiError(
      'Network error: Unable to reach the server',
      0,
      error
    );
  }
}

/**
 * Fetch a blob response with authentication
 * Used for file downloads (Excel, PDF, etc.)
 */
export async function apiFetchBlob(
  endpoint: string,
  options: RequestInit = {}
): Promise<Blob> {
  const url = `${API_URL}${endpoint}`;

  // Get token dynamically from Auth0
  const token = tokenGetter ? await tokenGetter() : null;

  // Build headers with auth and optional impersonation
  const blobImpersonationUserId = localStorage.getItem('impersonation_user_id');
  const headers: HeadersInit = {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(blobImpersonationUserId ? { 'X-Impersonate-User-Id': blobImpersonationUserId } : {}),
    ...options.headers,
  };

  if (import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true') {
    console.log(`[API] ${options.method || 'GET'} ${endpoint} (blob)`);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle non-2xx responses
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        // Response wasn't JSON, use status text
      }

      throw new ApiError(errorMessage, response.status);
    }

    return await response.blob();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    console.error('[API] Request failed:', error);
    throw new ApiError(
      'Network error: Unable to reach the server',
      0,
      error
    );
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <T = any>(endpoint: string) =>
    apiFetch<T>(endpoint, { method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = any>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(endpoint: string) =>
    apiFetch<T>(endpoint, { method: 'DELETE' }),

  /** PUT a multipart form, e.g. a file upload */
  putForm: <T = any>(endpoint: string, form: FormData) =>
    apiFetch<T>(endpoint, { method: 'PUT', body: form }),

  getBlob: (endpoint: string) =>
    apiFetchBlob(endpoint, { method: 'GET' }),
};
